import {
  CaseListDto,
  DataBlockVariant,
  DataBlockWithValuesDto,
  QueryFactory,
  SortDirection,
  SortOrder,
  useDataBlockType,
  VisitListDto,
} from '@lmt/shared-types';
import { localizeCaseTitle } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormComponentProps } from '../../global-forms/GlobalForms.types';
import { getFormattedDateTime } from '../../patients/common';
import clsx from 'clsx';
import globalStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import styles from './CopyDataBlockFieldValue.module.scss';
import { Button, ButtonColor } from '../../common/ui-kit/buttons/Button';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { UIEditorLazy } from '../../editor/UIEditorLazy';
import { getDataBlockTypeNameForLocalization } from '../common';
import { useGlobalForms } from '../../global-forms/useGlobalForms';
import { RadioButton } from '../../common/ui-kit/RadioButton';
import { isEmpty } from './common';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';

export type CopyFieldValueProps = FormComponentProps & {
  patientId: number;
  typeId: number;
  fieldId: number;
  config: string;
  typeName?: string;
};

export const CopyDataBlockFieldValueForm: FC<CopyFieldValueProps> = (props) => {
  const {
    formType,
    formId,
    tryCompleteForm,
    patientId,
    typeId,
    fieldId,
    config,
    typeName,
  } = props;
  const i18nextGlobal = useTranslation();
  const i18next = useScopedTranslation('data_blocks.copy_previous_fields');
  const forms = useGlobalForms();
  const visitTypes = useVisitTypes();
  const dataBlockType = useDataBlockType(typeId)!;
  const [values, setValues] = useState<{
    [key: string]: string | undefined;
  }>({});
  const [selectedKey, setSelectedKey] = useState<string | undefined>();
  const dataBlockFieldQuery =
    QueryFactory.DataBlockFieldQuery.useGetByIdQuery(fieldId);
  const casesQuery = QueryFactory.CaseQuery.useGetCasesQuery({
    patientId: props.patientId,
    includeValues: true,
    sorting_SortField: 'id',
    sorting_SortDirection: SortDirection.Desc,
  });
  const visitsQuery = QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery({
    patientId: patientId,
    includeValues: true,
    sortBy: 'start',
    sortOrder: SortOrder.Desc,
  });
  const dataBlocksQuery = QueryFactory.DataBlockQuery.useGetWithValuesQuery(
    {
      typeId: typeId,
      patientId: patientId,
      sorting_SortField: 'id',
      sorting_SortDirection: SortDirection.Desc,
    },
    {
      enabled: dataBlockType.variant !== DataBlockVariant.SharedFields,
    },
  );

  const isLoading =
    dataBlockFieldQuery.isFetching ||
    casesQuery.isFetching ||
    visitsQuery.isFetching ||
    dataBlocksQuery.isFetching;

  function getTitle(
    pCase: CaseListDto,
    visit?: VisitListDto,
    dataBlock?: DataBlockWithValuesDto,
  ) {
    let title = localizeCaseTitle(pCase);
    if (visit) {
      const visitTitle = i18nextGlobal.t('patients.cases.visits.title', {
        visit: i18nextGlobal.t(
          (visitTypes.find((visitType) => visitType.id === visit.typeId)
            ?.value || '-') as any,
        ),
        date: getFormattedDateTime(visit.start),
      });
      title = [visitTitle, title].join(', ');
    }
    if (dataBlock) {
      if (dataBlock.begin) {
        const blockTitle = i18next.t('measurement_title', {
          value: dataBlockType.value.toUpperCase(),
          date: getFormattedDateTime(dataBlock.begin),
        });
        title = [blockTitle, title].join(', ');
      }
    }
    return title;
  }

  const closeForm = useCallback(() => {
    forms.closeForm(formId);
  }, [formId]);

  function getInputValues(name: string, value: string | undefined) {
    const inputValues: { [key: string]: string | undefined } = {};
    inputValues[name] = value;
    return inputValues;
  }
  useEffect(
    function getFieldValuesFromPreviosDataBlocks() {
      if (isLoading) {
        return;
      }
      const fieldName = dataBlockFieldQuery.data!.name;
      const cases = casesQuery.data!.data;
      const visits = visitsQuery.data!;
      const dictionary: { [key: string]: string | undefined } = {};
      if (dataBlockType.variant === DataBlockVariant.SharedFields) {
        if (dataBlockType.value === 'Case') {
          cases.forEach((pCase) => {
            if (pCase.values && !isEmpty(pCase.values[fieldName])) {
              dictionary[getTitle(pCase)] = pCase.values[fieldName];
            }
          });
        } else if (dataBlockType.value === 'Visit') {
          visits.forEach((visit) => {
            if (visit.values && !isEmpty(visit.values[fieldName])) {
              const pCase = casesQuery.data!.data.find(
                (c) => c.id === visit.caseId,
              )!;
              dictionary[getTitle(pCase, visit)] = visit.values[fieldName];
            }
          });
        } else {
          closeForm();
        }
      } else if (dataBlockType.variant === DataBlockVariant.Patient) {
        closeForm();
      } else {
        const dataBlocks = dataBlocksQuery.data!.data;
        dataBlocks.forEach((dataBlock) => {
          if (!isEmpty(dataBlock.values[fieldName])) {
            const pCase = casesQuery.data!.data.find(
              (c) => c.id === dataBlock.caseId,
            )!;
            const visit = visitsQuery.data!.find(
              (v) => v.id === dataBlock.visitId,
            );
            dictionary[getTitle(pCase, visit, dataBlock)] =
              dataBlock.values[fieldName];
          }
        });
      }
      if (Object.keys(dictionary).length === 0) {
        closeForm();
      }
      dictionary[i18next.t('empty_field_value')] = undefined;
      setValues(dictionary);
    },
    [
      isLoading,
      dataBlockFieldQuery.data,
      casesQuery.data,
      visitsQuery.data,
      dataBlocksQuery.data,
    ],
  );

  const saveDataBlock = useCallback(async () => {
    await tryCompleteForm(
      formType,
      formId,
      async () => {
        if (selectedKey) {
          return values[selectedKey];
        }
      },
      i18nextGlobal.t('data_blocks.loading'),
    ).catch((e) => console.log(e));
  }, [values, selectedKey, formType, formId]);

  return (
    <div
      className={clsx(globalStyles.autoSizedContainer, styles.container)}
      data-test-id="createDataBlockPopup"
    >
      <div className={clsx(globalStyles.dataContainer, styles.dataContainer)}>
        <div className={styles.headerContainer}>
          <div
            className={styles.header}
            data-test-id="copyingDataBlockEmptyFieldInfo"
          >
            {i18next.t('header', { name: dataBlockFieldQuery.data?.name })}
          </div>
        </div>

        {dataBlockFieldQuery.data &&
          Object.keys(values).map((title) => (
            <div
              key={title}
              className={styles.blockContainer}
              data-test-id="copyingDataBlockFieldContainer"
            >
              <div
                className={styles.radiobutton}
                data-test-id="copyingDataBlockFieldHeader"
              >
                <RadioButton
                  name={createId()}
                  checked={title === selectedKey}
                  onChange={() => setSelectedKey(title)}
                />
                <div className={styles.title}>{title}</div>
              </div>

              <div>
                <UIEditorLazy
                  dataBlockTypeNameForLocalization={
                    typeName ??
                    getDataBlockTypeNameForLocalization(dataBlockType)
                  }
                  dataBlockType={dataBlockType}
                  isOnlyView={true}
                  isInDesignerMode={false}
                  config={config}
                  inputValues={getInputValues(
                    dataBlockFieldQuery.data.name,
                    values[title],
                  )}
                  sharedValues={getInputValues(
                    dataBlockFieldQuery.data.name,
                    values[title],
                  )}
                />
              </div>
            </div>
          ))}
      </div>
      <div className={globalStyles.submitContainer}>
        <Button
          onClick={closeForm}
          title={i18next.t('cancel_button')}
          color={ButtonColor.Primary}
          className={globalStyles.submit}
        />
        <Button
          disabled={!selectedKey}
          onClick={saveDataBlock}
          title={i18next.t('save_button')}
          className={globalStyles.submit}
        />
      </div>
    </div>
  );
};
